import debounce from 'debounce';
import Api from '../../api/api'
import { IMAGE_URL } from '../../api/routes';

const PAGE_LIMIT = 100;
let currentSubscription = null;

const fetchEditionTracks = async ({hitlistId, editionId, page, pagination, query}) => {
  const params = {q: query}
  if (pagination) {
    params.from = pagination.start
    params.to = pagination.end
    params.limit = pagination.end - pagination.start + 1
  } else {
    params.limit = PAGE_LIMIT
    params.page = page
  }

  const response = await Api.get(`/2.0/lists/${hitlistId}/editions/${editionId}/tracks/played`, {params});
  const { data: { tracks, total, pdf, itunes, spotify } } = response;
  const newTracks = tracks
    .map(track => {
      const hitlist = track.occurences.list.find((list) => list.id == hitlistId)
      const edition = hitlist && hitlist.editions.find((edition) => edition.id == editionId)
      const stats = edition && {
       previousPosition: edition.previous_position,
       timesInList: edition.times_in_list
      }

      return {
        id: track.id,
        position: track.position,
        thumbnail: track.thumbnail,
        artist: track.artist.name,
        title: track.title,
        selectorCode: track.selector_code,
        slug: track.slug,
        stats,
        hooks: {
          spotify: track.spotify_url,
          itunes: track.affiliate_url,
          m4a: track.hooks ? track.hooks.m4a : undefined,
        }
      }
    });

    return {
      tracks: newTracks,
      total,
      pdf,
      hooks: {
        itunes,
        spotify
      }
    }
}

export default {
  namespaced: true,
  state: {
    isLoading: false,
    hitlistId: null,
    editionId: null,
    editions: [],
    page: 1,
    tracks: [],
    searchedTracks: [],
    searchPage: 1,
    pdf: null,
    hooks: {},
    total: null, // total number of tracks available in the hitlist
    firstVisiblePosition: null,
    pagination: null,
    title: '',
    description: '',
    image: null,
    query: null,
  },
  getters: {
    liveTrack(state, rootGetters, rootState) {
      // Not using mainChannel getter because that fucks with reactivity
      const activeChannel = rootState.channels.channels.find(channel => channel.id === rootState.stations.stationId)
      const lastPlay = activeChannel && activeChannel.lastPlay

      const editionInfo = lastPlay && lastPlay.active_editions.find(({edition: {id: editionId}}) => editionId == state.editionId )

      if (editionInfo) {
        return {
          position: editionInfo.position,
          ...lastPlay
        }
      }
    },
    displayedTracks({ tracks, searchedTracks }, {isSearching}) {
      if (isSearching) {
        return searchedTracks
      } else {
        return tracks
      }
    },
    hasMore(state, {displayedTracks, isSearching}) {
      return state.isLoading === false && !isSearching && (displayedTracks.length < state.total)
    },
    isSearching({query}) {
      return query && query.length > 1
    },
    hasMultipleEditions({editions}) {
      return editions.length > 1
    }
  },
  actions: {
    async fetchEditions({ state, commit, dispatch }) {
      if (state.isLoading) { return; }
      commit('LOAD_START');
      try {
        const response = await Api.get(`/2.0/lists/${state.hitlistId}/editions?order=reverse`);
        const { data: { editions, list } } = response;
        const newEditions = editions
          // only show editions starting before current time
          .map(edition => ({
            name: edition.name,
            id: edition.id,
          }));
        const editionId = !state.editionId && editions.length ? editions[0].id : state.editionId;
        const image = `${IMAGE_URL}${list.image}`;
        commit('EDITIONS_FETCHED', { editions: newEditions, editionId, title: list.name, description: list.description, image });
        // Update tracks
        dispatch('fetchTracks');
      } catch (error) {
        console.error(error); // eslint-disable-line
        commit('LOAD_FAIL');
      }
    },
    async fetchTracks({ state, commit }) {
      if (state.isLoading) { return; }
      commit('LOAD_START');
      try {
        const trackData = await fetchEditionTracks({ hitlistId: state.hitlistId, editionId: state.editionId, page: state.page, pagination: state.pagination })
        if (state.pagination) {
          commit('TRACKS_REPLACE', trackData)
        } else {
          commit('TRACKS_FETCHED', trackData);
        }
      } catch (error) {
        console.error(error); // eslint-disable-line
        commit('LOAD_FAIL');
      }
    },
    searchTracks: debounce(async ({ state, commit }) => {
      try {
        if (state.query) {
          const trackData = await fetchEditionTracks({ hitlistId: state.hitlistId, editionId: state.editionId, page: state.searchPage, query: state.query})
          commit('TRACKS_SEARCHED', trackData)
        } else {
          commit('TRACKS_SEARCHED', {tracks: [], total: null})
        }
      } catch(error) {
        console.error(error)
        commit('LOAD_FAIL')
      }
    }, 250),
    fetchPaginated({ commit, dispatch }, {start, end}) {
      commit('SET_PAGINATION', {start, end})
      commit('TRACKS_CLEAR')
      dispatch('fetchTracks')
    },
    fetchNext({ state, dispatch, commit }) {
      const { tracks, total, page } = state;
      if (tracks.length > total) {
        return;
      }
      commit('SET_PAGE', { page: page + 1 });
      dispatch('fetchTracks');
    },
    setHitlist({ commit, dispatch }, payload) {
      commit('SET_HITLIST', payload);
      dispatch('fetchEditions');

      if (currentSubscription) {
        currentSubscription.unsubscribe();
      }

      currentSubscription = window.Site.socket.subscribe({edition: payload.editionId}, 'play', (play) => {
        play.track.artist = play.track.artist.name
        play.track.position = play.position
        commit('ADD_TRACK', play.track);
      })
    },
    search({ commit, dispatch }, { query }) {
      const queryValue = query.trim().toLowerCase();

      commit('SET_QUERY', { query: queryValue === '' ? null : queryValue });

      if (queryValue && queryValue.length > 1) {
        commit('LOAD_START');
        dispatch('searchTracks')
      }
    },
  },
  mutations: {
    EDITIONS_FETCHED(state, { editions, editionId, title, description, image }) {
      state.editions = editions;
      state.editionId = editionId;
      state.tracks = [];
      state.title = title;
      state.description = description;
      state.image = image;
      state.isLoading = false;
      state.page = 1;
    },
    ADD_TRACK(state, track) {
      state.tracks.unshift(track);
    },
    TRACKS_FETCHED(state, { tracks, total, hooks, pdf }) {
      state.tracks = state.tracks.concat(tracks);
      state.hooks = hooks
      state.pdf = pdf
      state.total = total;
      state.isLoading = false;
      if (tracks.length) {
        state.firstVisiblePosition = state.firstVisiblePosition ? Math.min(state.firstVisiblePosition, tracks[0].position) : tracks[0].position
      }
    },
    TRACKS_REPLACE(state, { tracks }) {
      state.tracks = tracks
      state.isLoading = false
    },
    TRACKS_CLEAR(state) {
      state.tracks = []
    },
    TRACKS_SEARCHED(state, { tracks }) {
      state.searchedTracks = tracks.sort((t1, t2) => t1.position - t2.position)
      state.isLoading = false
    },
    SET_HITLIST(state, { hitlistId, editionId }) {
      state.hitlistId = hitlistId;
      state.editionId = editionId;
      state.tracks = [];
      state.page = 1;
    },
    SET_PAGE(state, { page }) {
      state.page = page;
    },
    SET_QUERY(state, { query }) {
      state.query = query;
    },
    SET_PAGINATION(state, { start, end }) {
      state.pagination = {start, end}
    },
    LOAD_START(state) {
      state.isLoading = true;
    },
    LOAD_FAIL(state) {
      state.isLoading = false;
    },
  }
}
